import React, { useEffect, useState } from 'react';
import { BarChart3, Ticket, CheckCircle2, XCircle, ChevronDown, ChevronUp, ScanLine, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';

const StatPill = ({ label, value, className }) => (
  <div className={`rounded-xl border px-4 py-3 text-center ${className}`}>
    <p className="text-2xl font-extrabold text-white">{value}</p>
    <p className="text-xs text-white/60 mt-0.5">{label}</p>
  </div>
);

export default function AdminEventStats() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [rosters, setRosters] = useState({});

  useEffect(() => {
    if (user && !['admin', 'owner', 'overseer'].includes(user.role)) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    api.get('/admin/tickets/stats')
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load attendance stats'))
      .finally(() => setLoading(false));
  }, []);

  const toggleRoster = async (eventId) => {
    if (expanded === eventId) {
      setExpanded(null);
      return;
    }
    setExpanded(eventId);
    if (!rosters[eventId]) {
      try {
        const res = await api.get(`/admin/events/${eventId}/tickets`);
        setRosters((prev) => ({ ...prev, [eventId]: res.data || [] }));
      } catch (e) {
        toast.error('Failed to load roster');
      }
    }
  };

  const totals = data?.totals;
  const events = data?.events || [];

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 relative z-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-purple-400" />
            <h1 className="text-3xl font-bold text-white">Event Attendance</h1>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/admin/tickets/scan')}
            data-testid="open-scanner-btn"
          >
            <ScanLine className="w-4 h-4 mr-2" /> Scanner
          </Button>
        </div>
        <p className="text-gray-400 mb-6">Tickets issued vs scanned at the door, per event.</p>

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto" />
          </div>
        ) : (
          <>
            {/* Totals */}
            {totals && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="stats-totals">
                <StatPill label="Tickets issued" value={totals.issued} className="bg-white/5 border-white/10" />
                <StatPill label="Scanned at door" value={totals.scanned} className="bg-green-500/10 border-green-500/30" />
                <StatPill label="Not yet scanned" value={totals.active} className="bg-purple-500/10 border-purple-500/30" />
                <StatPill label="Overall scan rate" value={`${totals.scan_rate}%`} className="bg-fuchsia-500/10 border-fuchsia-500/30" />
              </div>
            )}

            {/* Per-event cards */}
            {events.length === 0 ? (
              <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10" data-testid="stats-empty">
                <Ticket className="w-14 h-14 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-400">No tickets issued yet. Stats appear once people RSVP to events.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="stats-event-list">
                {events.map((ev) => (
                  <div
                    key={ev.event_id}
                    className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden"
                    data-testid={`stats-event-${ev.event_id}`}
                  >
                    <button
                      onClick={() => toggleRoster(ev.event_id)}
                      className="w-full text-left p-5 hover:bg-white/5 transition-colors"
                      data-testid={`stats-event-toggle-${ev.event_id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <h3 className="text-lg font-bold text-white truncate">{ev.event_title}</h3>
                          <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
                            <CalendarDays className="w-3.5 h-3.5" />
                            {ev.event_date} {ev.event_time} {ev.event_location ? `— ${ev.event_location}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-sm font-bold text-white">
                            {ev.scanned}/{ev.issued - ev.cancelled}
                          </span>
                          <span className="text-xs text-gray-400">scanned</span>
                          {expanded === ev.event_id ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>
                      {/* Scan-rate bar */}
                      <div className="mt-3 h-2.5 bg-black/40 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-purple-500 to-green-400 rounded-full transition-all"
                          style={{ width: `${ev.scan_rate}%` }}
                        />
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs">
                        <span className="text-green-400">{ev.scanned} scanned</span>
                        <span className="text-purple-300">{ev.active} unscanned</span>
                        {ev.cancelled > 0 && <span className="text-gray-500">{ev.cancelled} cancelled</span>}
                        <span className="text-white/70 ml-auto font-semibold">{ev.scan_rate}% scan rate</span>
                      </div>
                    </button>

                    {/* Expandable roster */}
                    {expanded === ev.event_id && (
                      <div className="border-t border-white/10 p-4" data-testid={`stats-roster-${ev.event_id}`}>
                        {!rosters[ev.event_id] ? (
                          <p className="text-sm text-gray-400 text-center py-3">Loading roster…</p>
                        ) : rosters[ev.event_id].length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-3">No tickets for this event.</p>
                        ) : (
                          <div className="space-y-1.5 max-h-72 overflow-y-auto">
                            {rosters[ev.event_id].map((t) => (
                              <div
                                key={t.ticket_id}
                                className="flex items-center gap-3 text-sm bg-black/25 rounded-lg px-3 py-2"
                              >
                                {t.status === 'scanned' ? (
                                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                                ) : t.status === 'cancelled' ? (
                                  <XCircle className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                ) : (
                                  <Ticket className="w-4 h-4 text-purple-300 flex-shrink-0" />
                                )}
                                <span className="text-white font-medium">{t.attendee_name}</span>
                                <span className="font-mono text-[11px] text-white/40 truncate">{t.ticket_id}</span>
                                <span className={`ml-auto text-xs capitalize ${
                                  t.status === 'scanned' ? 'text-green-400' : t.status === 'cancelled' ? 'text-gray-500' : 'text-purple-300'
                                }`}>
                                  {t.status === 'scanned' && t.scanned_at
                                    ? `scanned ${new Date(t.scanned_at).toLocaleTimeString()}`
                                    : t.status}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
